import { CheckListIcon, H1Icon, H2Icon, H3Icon, H4Icon, SmallIcon, SupIcon } from "./components/svg"
import { Link, RichTextExt, blockPluginConstructor, markPluginConstructor, plugins, types } from "react-bricks/frontend"
import { RenderElementProps, RenderLeafProps } from "slate-react"

import React from "react"
import { cn } from "../shared/utils/cn"

type TypographySize = "P1" | "P2" | "P3" | "UI1" | "UI2" | "UI3" | "UI4"
type TypographyAllowItem =
    | "S1"
    | "S2"
    | "S3"
    | "S4"
    | "S5"
    | "S6"
    | "link"
    | "bold"
    | "italic"
    | "small"
    | "sup"
    | "ul"
    | "cl"

type TypographyProps = {
    size?: TypographySize
    allowList?: TypographyAllowItem[]
    propName?: string
    id?: string
    hidden?: boolean
    className?: string
}

type TypographyText = {
    text?: string
    bold?: true
    sup?: true
    italic?: true
    type?: "li" | "cli"
    S1?: true
    S2?: true
    S3?: true
    S4?: true
    S5?: true
    S6?: true
    children?: TypographyText[]
}

export type TypographyElement = {
    type: "paragraph" | "h1" | "h2" | "h3" | "h4" | "link" | "small" | "ul" | "cl"
    children: TypographyText[]
}

const Typography = ({
    size = "P1",
    allowList = ["S1", "S2", "S3", "S4", "S5", "S6", "link", "bold", "italic", "small", "sup", "ul", "cl"],
    propName = "text",
    id,
    hidden = false,
    className,
}: TypographyProps) => {
    const { heading1, heading2, heading3, heading4, heading5, heading6, unorderedList, link, bold, italic } = plugins

    const sup = markPluginConstructor({
        name: "sup",
        label: "Superscript",
        render: ({ children, attributes }: RenderLeafProps) => <sup {...attributes}>{children}</sup>,
        icon: SupIcon,
    })

    const small = blockPluginConstructor({
        name: "small",
        label: "Small",
        render: ({ children, attributes }: RenderElementProps) => (
            <small {...attributes} className="mt-10 block text-xs first:mt-0">
                {children}
            </small>
        ),
        icon: SmallIcon,
    })

    const checkList = blockPluginConstructor({
        name: "cl",
        label: "Check list",
        itemName: "cli",
        render: ({ children, attributes }: RenderElementProps) => (
            <ul {...attributes} className="mb-3.5 list-none last:mb-0 sm:mb-5">
                {children}
            </ul>
        ),
        renderItem: ({ children, attributes }: RenderElementProps) => (
            <li
                {...attributes}
                data-before=""
                className="relative mb-5 pl-7 before:absolute before:left-0 before:top-0 before:z-2 before:font-['lavita-icon-font'] before:text-md before:content-[attr(data-before)] last:mb-0 lg:mb-7 lg:pl-10 before:lg:text-xl"
            >
                {children}
            </li>
        ),
        icon: CheckListIcon,
    })

    const headingSize1 = markPluginConstructor({
        name: "S1",
        label: "Size 1",
        render: ({ children, attributes }: RenderLeafProps) => (
            <span
                {...attributes}
                className="last:inline-block font-gibson text-5xl font-semibold mb-7 xs:text-5.5xl sm:text-6xl sm:mb-10 lg:text-7xl xl:text-8xl"
            >
                {children}
            </span>
        ),
        icon: heading1.button?.icon,
    })

    const headingSize2 = markPluginConstructor({
        name: "S2",
        label: "Size 2",
        render: ({ children, attributes }: RenderLeafProps) => (
            <span
                {...attributes}
                className="last:inline-block font-gibson text-3.5xl font-semibold mb-7 xs:text-5xl sm:text-5.5xl sm:mb-10 lg:text-6xl"
            >
                {children}
            </span>
        ),
        icon: heading2.button?.icon,
    })

    const headingSize3 = markPluginConstructor({
        name: "S3",
        label: "Size 3",
        render: ({ children, attributes }: RenderLeafProps) => (
            <span
                {...attributes}
                className="last:inline-block font-gibson text-3.5xl font-semibold mb-7 md:text-5xl lg:text-5.5xl lg:mb-10"
            >
                {children}
            </span>
        ),
        icon: heading3.button?.icon,
    })

    const headingSize4 = markPluginConstructor({
        name: "S4",
        label: "Size 4",
        render: ({ children, attributes }: RenderLeafProps) => (
            <span
                {...attributes}
                className="last:inline-block font-gibson text-xl font-semibold mb-5 sm:text-3xl lg:text-5xl lg:mb-7"
            >
                {children}
            </span>
        ),
        icon: heading4.button?.icon,
    })

    const headingSize5 = markPluginConstructor({
        name: "S5",
        label: "Size 5",
        render: ({ children, attributes }: RenderLeafProps) => (
            <span
                {...attributes}
                className="last:inline-block font-gibson text-lg font-semibold mb-5 sm:text-xl lg:text-3xl lg:mb-6"
            >
                {children}
            </span>
        ),
        icon: heading5.button?.icon,
    })

    const headingSize6 = markPluginConstructor({
        name: "S6",
        label: "Size 6",
        render: ({ children, attributes }: RenderLeafProps) => (
            <span
                {...attributes}
                className="last:inline-block font-gibson text-md font-semibold mb-3.5 sm:text-lg lg:text-xl lg:mb-5"
            >
                {children}
            </span>
        ),
        icon: heading6.button?.icon,
    })

    const allowedPlugins: types.RichTextPlugin[] = [
        {
            ...link,
            renderElement: ({ children, attributes, element }: RenderElementProps) => (
                <Link
                    {...attributes}
                    href={element.data?.url || element.url}
                    target={element.data?.isTargetBlank ? "_blank" : undefined}
                    rel={element.data?.isTargetBlank ? "noopener" : undefined}
                    className="link [&:has(sup)]:whitespace-nowrap [&:not(:has(sup))]:underline hover:[&_sup]:underline"
                    tabIndex={hidden ? -1 : undefined}
                >
                    {children}
                </Link>
            ),
        },
        {
            ...bold,
            renderLeaf: ({ children, attributes }: RenderLeafProps) => (
                <strong {...attributes} className="font-semibold">
                    {children}
                </strong>
            ),
        },
        italic,
        small,
        sup,
        {
            ...unorderedList,
            renderElement: ({ children, attributes }: RenderElementProps) => (
                <ul {...attributes} className="mb-3.5 list-outside list-disc last:mb-0 sm:mb-5">
                    {children}
                </ul>
            ),
            renderItemElement: ({ children, attributes }: RenderElementProps) => (
                <li {...attributes} className="mb-3 last:mb-0 sm:mb-3.5 lg:mb-5 ml-5 lg:ml-7">
                    {children}
                </li>
            ),
        },
        checkList,
        headingSize1,
        headingSize2,
        headingSize3,
        headingSize4,
        headingSize5,
        headingSize6,
    ].filter(p => allowList.includes(p.name as TypographyAllowItem))

    return (
        <div
            id={id}
            className={cn({
                "text-md sm:text-lg lg:text-xl": size === "P1",
                "text-base sm:text-md lg:text-lg": size === "P2",
                "text-base sm:text-md": size === "P3",
                "text-md xs:text-lg sm:text-xl lg:text-3xl/3xl": size === "UI1",
                "text-sm lg:text-base": size === "UI2",
                "text-sm": size === "UI3",
                "text-xs sm:text-sm": size === "UI4",
            },
                className)}
        >
            <RichTextExt
                propName={propName}
                renderBlock={({ children, attributes }) => (
                    <p {...attributes} className="mb-3.5 last:mb-0 sm:mb-5">
                        {children}
                    </p>
                )}
                placeholder=""
                plugins={[
                    {
                        ...heading1,
                        // @ts-ignore
                        button: { ...heading1.button, icon: H1Icon },
                    },
                    {
                        ...heading2,
                        // @ts-ignore
                        button: { ...heading2.button, icon: H2Icon },
                    },
                    {
                        ...heading3,
                        // @ts-ignore
                        button: { ...heading3.button, icon: H3Icon },
                    },
                    {
                        ...heading4,
                        // @ts-ignore
                        button: { ...heading4.button, icon: H4Icon },
                    },
                    ...allowedPlugins,
                ]}
            />
        </div>
    )
}

export default Typography
